/**
 * DispatchSectionsPanel — Phase A Site Management UI.
 *
 * Toggle which dispatch sections are enabled for a given site.
 *  • Salem: locked (legacy single Dispatch flow stays untouched).
 *  • Other sites: Super Admin can tick/untick from the catalog
 *    (color_combo / art_silk / kids).
 *
 * Each enabled section unlocks per-section UI tiles + per-section
 * MongoDB collections in subsequent phases (B onwards).
 */
import React, { useEffect, useState } from 'react';
import apiClient from '@/services/apiClient';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Loader2, Layers, Save } from 'lucide-react';

export default function DispatchSectionsPanel({ siteId, isSalem }) {
  const [catalog, setCatalog] = useState([]);
  const [enabled, setEnabled] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [c, e] = await Promise.all([
          apiClient.get('/dispatch-sections/catalog'),
          apiClient.get(`/dispatch-sections/sites/${siteId}/enabled`),
        ]);
        if (cancelled) return;
        setCatalog(c.data?.sections || []);
        setEnabled(e.data?.enabled || []);
        setDirty(false);
      } catch (err) {
        if (!cancelled) toast.error(err.response?.data?.detail || 'Failed to load dispatch sections');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [siteId]);

  const toggle = (slug) => {
    if (isSalem) return;
    setEnabled((prev) => {
      const next = prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug];
      setDirty(true);
      return next;
    });
  };

  const onSave = async () => {
    setSaving(true);
    try {
      const r = await apiClient.put(`/dispatch-sections/sites/${siteId}/enabled`, {
        enabled,
      });
      setEnabled(r.data?.enabled || []);
      setDirty(false);
      toast.success('Dispatch sections updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="rounded-lg border border-stone-200 bg-white p-4"
      data-testid={`dispatch-sections-panel-${siteId}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-stone-700">
          <Layers className="h-4 w-4" />
          Dispatch Sections
          <span className="text-xs font-normal text-stone-500">
            ({enabled.length} of {catalog.length} enabled)
          </span>
        </div>
        {!isSalem && (
          <Button
            size="sm"
            onClick={onSave}
            disabled={!dirty || saving}
            data-testid={`save-dispatch-sections-${siteId}`}
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Save className="h-3.5 w-3.5 mr-1" />}
            Save
          </Button>
        )}
      </div>

      {isSalem && (
        <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 mb-3">
          <strong>Salem is locked</strong> — the legacy single Dispatch flow remains in
          effect. Enable catalog sections only on non-Salem sites.
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-stone-500 text-sm py-4">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {catalog.map((s) => {
            const checked = enabled.includes(s.slug);
            return (
              <label
                key={s.slug}
                className={`flex items-start gap-2 p-3 rounded-md border cursor-pointer transition ${
                  checked
                    ? 'border-emerald-400 bg-emerald-50'
                    : 'border-stone-200 hover:border-stone-300 bg-white'
                } ${isSalem ? 'opacity-60 cursor-not-allowed' : ''}`}
                data-testid={`dispatch-section-toggle-${siteId}-${s.slug}`}
              >
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={checked}
                  disabled={isSalem || saving}
                  onChange={() => toggle(s.slug)}
                  data-testid={`dispatch-section-checkbox-${siteId}-${s.slug}`}
                />
                <div className="min-w-0">
                  <div className="text-sm font-medium text-stone-800">{s.display_name}</div>
                  <div className="text-xs text-stone-500 mt-0.5">{s.description}</div>
                  <div className="text-[10px] font-mono text-stone-400 mt-1">slug: {s.slug}</div>
                </div>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}
